/**
 * Reusable Employee Timeline component.
 * Usage: <EmployeeTimeline employeeId={id} />
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Chip, Collapse,
  IconButton, Tooltip, TextField, MenuItem,
} from '@mui/material';
import { ExpandMore, ExpandLess, FilterList } from '@mui/icons-material';

function apiFetch(path: string) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CATEGORY_LABELS: Record<string, string> = {
  hire: 'Hire', salary: 'Salary', org_change: 'Org Change', performance: 'Performance',
  probation: 'Probation', training: 'Training', contract: 'Contract',
  recognition: 'Recognition', leave: 'Leave', disciplinary: 'Disciplinary',
  loan: 'Loan', certification: 'Certification', pip: 'PIP', absence: 'Absence',
};

interface Event {
  date: string; category: string; icon: string; color: string;
  title: string; subtitle?: string; detail?: string; isHighlight?: boolean;
}

interface Props { employeeId: string; compact?: boolean; defaultCollapsed?: boolean }

export default function EmployeeTimeline({ employeeId, compact = false, defaultCollapsed = false }: Props) {
  const [events, setEvents]     = useState<Event[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const [catFilter, setCatFilter] = useState('');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/employee-timeline/${employeeId}`)
      .then(d => setEvents(d ?? []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [employeeId]);

  const filtered = catFilter ? events.filter(e => e.category === catFilter) : events;

  // Group by year
  const byYear: Record<string, Event[]> = {};
  filtered.forEach(e => {
    const yr = e.date.slice(0, 4);
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(e);
  });
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));

  const categories = [...Object.entries(
    events.reduce((acc: Record<string, boolean>, e) => { acc[e.category] = true; return acc; }, {})
  )].map(([k]) => k).sort();

  if (loading) return <CircularProgress size={20} />;
  if (error)   return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setExpanded(p => !p)}>
          <Typography variant="subtitle1" fontWeight={700}>Employee Timeline</Typography>
          <Chip size="small" label={`${events.length} events`} sx={{ fontSize: '0.65rem' }} />
          <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>
        {!compact && (
          <TextField select size="small" label="Filter" value={catFilter}
            onChange={e => setCatFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All Events</MenuItem>
            {categories.map(c => (
              <MenuItem key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      <Collapse in={expanded}>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No events recorded yet.</Typography>
        ) : (
          years.map(yr => (
            <Box key={yr}>
              {/* Year divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: yr === years[0] ? 0 : 2 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: '#e0e0e0' }} />
                <Chip label={yr} size="small" sx={{ fontSize: '0.7rem', bgcolor: '#f5f5f5', fontWeight: 700 }} />
                <Box sx={{ flex: 1, height: 1, bgcolor: '#e0e0e0' }} />
              </Box>

              {byYear[yr].map((ev, idx) => {
                const gIdx = filtered.indexOf(ev);
                const isExpanded = expandedIdx === gIdx;
                return (
                  <Box key={gIdx} sx={{ display: 'flex', gap: 1.5, mb: compact ? 1 : 1.5, alignItems: 'flex-start' }}>
                    {/* Timeline dot + line */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32 }}>
                      <Box sx={{
                        width: ev.isHighlight ? 32 : 24,
                        height: ev.isHighlight ? 32 : 24,
                        borderRadius: '50%',
                        bgcolor: ev.color + '20',
                        border: `2px solid ${ev.color}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: ev.isHighlight ? 14 : 11,
                        boxShadow: ev.isHighlight ? `0 0 0 3px ${ev.color}20` : undefined,
                      }}>
                        {ev.icon}
                      </Box>
                      {idx < byYear[yr].length - 1 && (
                        <Box sx={{ width: 2, flex: 1, minHeight: 20, bgcolor: '#e0e0e0', mt: 0.5 }} />
                      )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, pb: compact ? 0.5 : 1,
                      borderLeft: ev.isHighlight ? `3px solid ${ev.color}` : undefined,
                      pl: ev.isHighlight ? 1 : 0,
                      bgcolor: ev.isHighlight ? ev.color + '08' : undefined,
                      borderRadius: ev.isHighlight ? 1 : 0, p: ev.isHighlight ? 0.75 : 0,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant={ev.isHighlight ? 'body1' : 'body2'} fontWeight={ev.isHighlight ? 700 : 600}>
                          {ev.title}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">{ev.date}</Typography>
                        <Chip size="small" label={CATEGORY_LABELS[ev.category] ?? ev.category}
                          sx={{ fontSize: '0.6rem', bgcolor: ev.color + '15', color: ev.color, height: 18 }} />
                      </Box>
                      {ev.subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{ev.subtitle}</Typography>
                      )}
                      {ev.detail && !compact && (
                        <>
                          <Typography variant="caption" sx={{ color: ev.color, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setExpandedIdx(isExpanded ? null : gIdx)}>
                            {isExpanded ? '▲ hide detail' : '▼ show detail'}
                          </Typography>
                          <Collapse in={isExpanded}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, pl: 1, borderLeft: `2px solid ${ev.color}40` }}>
                              {ev.detail}
                            </Typography>
                          </Collapse>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Collapse>
    </Box>
  );
}
